import { useCallback, type MouseEvent as ReactMouseEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useBookmark, PENDING_BOOKMARK_KEY } from '../context/UserRacesContext';
import { BookmarkIcon } from './BookmarkIcon';
import './BookmarkButton.css';

interface BookmarkButtonProps {
  raceId: string;
  /** Used for aria-label / tooltip — pass race.nameLocal or race.name */
  raceName?: string;
  size?: 'sm' | 'md';
}

export function BookmarkButton({ raceId, raceName, size = 'md' }: BookmarkButtonProps) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { bookmarked, loading, toggle } = useBookmark(raceId);

  const openLoginModal = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set('login', '1');
    navigate(location.pathname + `?${params.toString()}` + location.hash);
  }, [location.pathname, location.search, location.hash, navigate]);

  const handleClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    // Prevent parent card/marker click handlers from firing.
    e.stopPropagation();
    if (!user) {
      // Remember intent so the bookmark is applied after the OAuth round-trip.
      sessionStorage.setItem(PENDING_BOOKMARK_KEY, raceId);
      openLoginModal();
      return;
    }
    void toggle();
  };

  const handleKey = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    // Prevent the keyboard event from bubbling to role="button" parents.
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
    }
  };

  const label = user
    ? bookmarked
      ? `${raceName ? `${raceName} ` : ''}북마크 해제`
      : `${raceName ? `${raceName} ` : ''}북마크 추가`
    : '로그인 후 북마크';

  const iconPx = size === 'sm' ? 16 : 18;

  if (!user) {
    return (
      <button
        type="button"
        className="bookmark-button is-guest"
        onClick={handleClick}
        onKeyDown={handleKey}
        title="로그인 후 북마크"
        aria-label={label}
        data-testid="bookmark-button"
      >
        <BookmarkIcon filled={false} size={iconPx} />
      </button>
    );
  }

  return (
    <button
      type="button"
      className="bookmark-button"
      onClick={handleClick}
      onKeyDown={handleKey}
      disabled={loading}
      aria-pressed={bookmarked}
      aria-label={label}
      title={bookmarked ? '북마크 해제' : '북마크'}
      data-testid="bookmark-button"
    >
      <BookmarkIcon filled={bookmarked} size={iconPx} />
    </button>
  );
}
